// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { SpacecraftInfo } from '../types';

export const SPACECRAFT: Record<string, SpacecraftInfo> = {
  VGR1: {
    code: 'VGR1',
    name: 'Voyager 1',
    mission: 'Interstellar exploration',
    agency: 'NASA / JPL',
    launched: '1977',
    category: 'interstellar',
    description: 'The most distant human-made object, now travelling through interstellar space beyond the heliopause. Returns faint signals via Canberra\'s DSS-43 70-metre dish.',
  },
  VGR2: {
    code: 'VGR2',
    name: 'Voyager 2',
    mission: 'Interstellar exploration',
    agency: 'NASA / JPL',
    launched: '1977',
    category: 'interstellar',
    description: 'Voyager 1\'s twin, the only spacecraft to have visited Uranus and Neptune. Its southern trajectory means only Canberra\'s DSS-43 can command it.',
  },
  NHPC: {
    code: 'NHPC',
    name: 'New Horizons',
    mission: 'Kuiper Belt flyby',
    agency: 'NASA / APL',
    launched: '2006',
    category: 'outer-planets',
    description: 'Flew past Pluto in 2015, then Kuiper Belt object Arrokoth in 2019. Still operational in the outer heliosphere.',
  },
  JNO: {
    code: 'JNO',
    name: 'Juno',
    mission: 'Jupiter orbiter',
    agency: 'NASA / JPL',
    launched: '2011',
    category: 'outer-planets',
    description: 'Polar orbiter studying Jupiter\'s interior, gravity field, and auroras since 2016.',
  },
  JUNO: {
    code: 'JUNO',
    name: 'Juno',
    mission: 'Jupiter orbiter',
    agency: 'NASA / JPL',
    launched: '2011',
    category: 'outer-planets',
    description: 'Polar orbiter studying Jupiter\'s interior, gravity field, and auroras since 2016.',
  },
  LUCY: {
    code: 'LUCY',
    name: 'Lucy',
    mission: 'Trojan asteroid tour',
    agency: 'NASA / SwRI',
    launched: '2021',
    category: 'asteroid',
    description: 'First mission to Jupiter\'s Trojan asteroids, flying past eight targets over a 12-year mission.',
  },
  PSYC: {
    code: 'PSYC',
    name: 'Psyche',
    mission: 'Metal asteroid orbiter',
    agency: 'NASA / JPL',
    launched: '2023',
    category: 'asteroid',
    description: 'Bound for the metallic asteroid 16 Psyche to study what may be an exposed planetary core.',
  },
  OSRX: {
    code: 'OSRX',
    name: 'OSIRIS-APEX',
    mission: 'Asteroid Apophis rendezvous',
    agency: 'NASA / GSFC',
    launched: '2016',
    category: 'asteroid',
    description: 'Formerly OSIRIS-REx, extended mission to asteroid Apophis after returning its Bennu sample capsule in 2023.',
  },
  DART: {
    code: 'DART',
    name: 'DART',
    mission: 'Kinetic impactor test',
    agency: 'NASA / APL',
    launched: '2021',
    category: 'asteroid',
    description: 'Successfully impacted Dimorphos in 2022, altering its orbit — the first planetary-defence demonstration.',
  },
  JWST: {
    code: 'JWST',
    name: 'James Webb Space Telescope',
    mission: 'Infrared space observatory',
    agency: 'NASA / ESA / CSA',
    launched: '2021',
    category: 'l1-l2',
    description: 'Flagship infrared telescope parked at Sun-Earth Lagrange point 2 (L2), 1.5 million km beyond Earth.',
  },
  CHDR: {
    code: 'CHDR',
    name: 'Chandra X-ray Observatory',
    mission: 'X-ray astronomy',
    agency: 'NASA / SAO',
    launched: '1999',
    category: 'earth-orbit',
    description: 'High-elliptical orbit X-ray observatory — one of NASA\'s Great Observatories.',
  },
  HST: {
    code: 'HST',
    name: 'Hubble Space Telescope',
    mission: 'Optical/UV observatory',
    agency: 'NASA / ESA',
    launched: '1990',
    category: 'earth-orbit',
    description: 'Iconic low-Earth orbit optical/UV/NIR telescope, still producing science after 35+ years.',
  },
  SOHO: {
    code: 'SOHO',
    name: 'SOHO',
    mission: 'Solar and heliospheric observatory',
    agency: 'NASA / ESA',
    launched: '1995',
    category: 'l1-l2',
    description: 'Solar observatory at L1 with a continuous view of the Sun; also the most prolific comet discoverer.',
  },
  SPP: {
    code: 'SPP',
    name: 'Parker Solar Probe',
    mission: 'Close solar encounter',
    agency: 'NASA / APL',
    launched: '2018',
    category: 'solar',
    description: 'Flies through the Sun\'s corona, the fastest human-made object ever.',
  },
  STA: {
    code: 'STA',
    name: 'STEREO-A',
    mission: 'Solar stereoscopic imaging',
    agency: 'NASA / APL',
    launched: '2006',
    category: 'solar',
    description: 'Heliocentric orbiter providing stereoscopic views of the Sun with STEREO-B (lost 2014).',
  },
  WIND: {
    code: 'WIND',
    name: 'Wind',
    mission: 'Solar wind monitor',
    agency: 'NASA / GSFC',
    launched: '1994',
    category: 'l1-l2',
    description: 'Long-lived L1 solar wind probe, key to space-weather forecasting.',
  },
  ACE: {
    code: 'ACE',
    name: 'ACE',
    mission: 'Solar wind composition',
    agency: 'NASA / GSFC',
    launched: '1997',
    category: 'l1-l2',
    description: 'Advanced Composition Explorer, also at L1; provides real-time geomagnetic storm warnings.',
  },
  IRIS: {
    code: 'IRIS',
    name: 'IRIS',
    mission: 'Solar chromosphere imager',
    agency: 'NASA / LMSAL',
    launched: '2013',
    category: 'earth-orbit',
    description: 'Interface Region Imaging Spectrograph — Sun-synchronous orbit probe of the solar chromosphere.',
  },
  M20: {
    code: 'M20',
    name: 'Mars 2020 / Perseverance',
    mission: 'Mars rover & Ingenuity base',
    agency: 'NASA / JPL',
    launched: '2020',
    category: 'mars',
    description: 'Rover in Jezero Crater collecting sample tubes for future return; carried the Ingenuity helicopter.',
  },
  MRO: {
    code: 'MRO',
    name: 'Mars Reconnaissance Orbiter',
    mission: 'Mars orbiter + comms relay',
    agency: 'NASA / JPL',
    launched: '2005',
    category: 'mars',
    description: 'High-resolution imaging orbiter and primary comms relay for surface assets.',
  },
  MVN: {
    code: 'MVN',
    name: 'MAVEN',
    mission: 'Mars upper atmosphere',
    agency: 'NASA / GSFC',
    launched: '2013',
    category: 'mars',
    description: 'Studies Mars\' upper atmosphere and its interaction with the solar wind.',
  },
  MAVN: {
    code: 'MAVN',
    name: 'MAVEN',
    mission: 'Mars upper atmosphere',
    agency: 'NASA / GSFC',
    launched: '2013',
    category: 'mars',
    description: 'Studies Mars\' upper atmosphere and its interaction with the solar wind.',
  },
  M01O: {
    code: 'M01O',
    name: 'Mars Odyssey',
    mission: 'Mars orbiter',
    agency: 'NASA / JPL',
    launched: '2001',
    category: 'mars',
    description: 'Longest-serving Mars orbiter; maps water, radiation, and relays rover data.',
  },
  TGO: {
    code: 'TGO',
    name: 'ExoMars TGO',
    mission: 'Mars trace-gas orbiter',
    agency: 'ESA / Roscosmos',
    launched: '2016',
    category: 'mars',
    description: 'Trace Gas Orbiter searching for methane and other biosignatures in the Martian atmosphere.',
  },
  EMM: {
    code: 'EMM',
    name: 'Emirates Mars Mission / Hope',
    mission: 'Mars atmospheric orbiter',
    agency: 'UAESA / MBRSC',
    launched: '2020',
    category: 'mars',
    description: 'UAE\'s first interplanetary mission, studying Martian weather and atmospheric escape.',
  },
  TIAN: {
    code: 'TIAN',
    name: 'Tianwen-1',
    mission: 'Mars orbiter/lander/rover',
    agency: 'CNSA',
    launched: '2020',
    category: 'mars',
    description: 'China\'s first successful Mars mission, including the Zhurong rover.',
  },
  LRO: {
    code: 'LRO',
    name: 'Lunar Reconnaissance Orbiter',
    mission: 'High-res lunar mapper',
    agency: 'NASA / GSFC',
    launched: '2009',
    category: 'moon',
    description: 'Flagship lunar orbiter — the highest-resolution map of the Moon to date.',
  },
  THMA: {
    code: 'THMA',
    name: 'THEMIS-A / ARTEMIS',
    mission: 'Lunar magnetosphere',
    agency: 'NASA / UC Berkeley',
    launched: '2007',
    category: 'moon',
    description: 'Two-spacecraft mission (ARTEMIS) studying the Moon\'s interaction with the solar wind.',
  },
  THMB: {
    code: 'THMB',
    name: 'THEMIS-B / ARTEMIS',
    mission: 'Lunar magnetosphere',
    agency: 'NASA / UC Berkeley',
    launched: '2007',
    category: 'moon',
    description: 'Two-spacecraft mission (ARTEMIS) studying the Moon\'s interaction with the solar wind.',
  },
  CAPS: {
    code: 'CAPS',
    name: 'CAPSTONE',
    mission: 'Lunar orbit pathfinder',
    agency: 'NASA / Advanced Space',
    launched: '2022',
    category: 'moon',
    description: 'CubeSat in near-rectilinear halo orbit around the Moon, demonstrating the orbit Gateway will use.',
  },
  KPLO: {
    code: 'KPLO',
    name: 'Danuri / KPLO',
    mission: 'Korean lunar orbiter',
    agency: 'KARI',
    launched: '2022',
    category: 'moon',
    description: 'South Korea\'s first lunar orbiter, carrying NASA\'s ShadowCam instrument.',
  },
  TESS: {
    code: 'TESS',
    name: 'TESS',
    mission: 'Exoplanet transit survey',
    agency: 'NASA / MIT',
    launched: '2018',
    category: 'earth-orbit',
    description: 'All-sky transit survey continuing Kepler\'s exoplanet search from a highly elliptical 2:1 lunar-resonance orbit.',
  },
  MMS1: {
    code: 'MMS1',
    name: 'MMS-1',
    mission: 'Magnetospheric Multiscale',
    agency: 'NASA / GSFC',
    launched: '2015',
    category: 'earth-orbit',
    description: 'One of four formation-flying probes studying magnetic reconnection in Earth\'s magnetosphere.',
  },
  MMS2: {
    code: 'MMS2',
    name: 'MMS-2',
    mission: 'Magnetospheric Multiscale',
    agency: 'NASA / GSFC',
    launched: '2015',
    category: 'earth-orbit',
    description: 'One of four formation-flying probes studying magnetic reconnection in Earth\'s magnetosphere.',
  },
  MMS3: {
    code: 'MMS3',
    name: 'MMS-3',
    mission: 'Magnetospheric Multiscale',
    agency: 'NASA / GSFC',
    launched: '2015',
    category: 'earth-orbit',
    description: 'One of four formation-flying probes studying magnetic reconnection in Earth\'s magnetosphere.',
  },
  MMS4: {
    code: 'MMS4',
    name: 'MMS-4',
    mission: 'Magnetospheric Multiscale',
    agency: 'NASA / GSFC',
    launched: '2015',
    category: 'earth-orbit',
    description: 'One of four formation-flying probes studying magnetic reconnection in Earth\'s magnetosphere.',
  },
  BEP: {
    code: 'BEP',
    name: 'BepiColombo',
    mission: 'Mercury orbiter (en route)',
    agency: 'ESA / JAXA',
    launched: '2018',
    category: 'inner-planets',
    description: 'Two orbiters currently cruising to Mercury, arriving 2026.',
  },
  AKA: {
    code: 'AKA',
    name: 'Akatsuki',
    mission: 'Venus climate orbiter',
    agency: 'JAXA',
    launched: '2010',
    category: 'inner-planets',
    description: 'Japan\'s Venus climate orbiter, studying the planet\'s super-rotating atmosphere.',
  },
  EC1: {
    code: 'EC1',
    name: 'Euclid',
    mission: 'Dark-energy survey',
    agency: 'ESA',
    launched: '2023',
    category: 'l1-l2',
    description: 'L2 wide-field optical/NIR telescope mapping the dark universe.',
  },
  CGO: {
    code: 'CGO',
    name: 'CAPSTONE (alt ID)',
    mission: 'Lunar orbit pathfinder',
    agency: 'NASA / Advanced Space',
    launched: '2022',
    category: 'moon',
    description: 'Also seen as CAPSTONE — CubeSat demonstrating Gateway\'s near-rectilinear halo orbit.',
  },
  DSN: {
    code: 'DSN',
    name: 'DSN (test / calibration)',
    mission: 'Ground-loop test',
    agency: 'NASA / JPL',
    launched: '—',
    category: 'other',
    description: 'Placeholder target used for maintenance, calibration, or idle dish activity.',
  },
  DSS: {
    code: 'DSS',
    name: 'DSS (self-test)',
    mission: 'Station self-test',
    agency: 'NASA / JPL',
    launched: '—',
    category: 'other',
    description: 'Placeholder target for unplanned maintenance or engineering upgrades.',
  },
};

export function getSpacecraftInfo(code: string): SpacecraftInfo {
  if (SPACECRAFT[code]) return SPACECRAFT[code];
  return {
    code,
    name: code,
    mission: 'Unknown / not in directory',
    agency: '—',
    launched: '—',
    category: 'other',
    description: 'This spacecraft code is not in the DSN Watch curated directory. It may be a new mission, a test target, or an internal identifier.',
  };
}

export const CATEGORY_COLOR: Record<SpacecraftInfo['category'], string> = {
  interstellar: '#f5a623',
  'outer-planets': '#e67e22',
  mars: '#e74c3c',
  moon: '#bdc3c7',
  'inner-planets': '#f1c40f',
  'earth-orbit': '#3498db',
  'l1-l2': '#9b59b6',
  solar: '#ff6b6b',
  asteroid: '#16a085',
  other: '#7f8c8d',
};

export const CATEGORY_LABEL: Record<SpacecraftInfo['category'], string> = {
  interstellar: 'Interstellar',
  'outer-planets': 'Outer planets',
  mars: 'Mars',
  moon: 'Moon',
  'inner-planets': 'Inner planets',
  'earth-orbit': 'Earth orbit',
  'l1-l2': 'L1 / L2',
  solar: 'Solar',
  asteroid: 'Asteroid',
  other: 'Other',
};
